/* eslint-disable @typescript-eslint/camelcase */
import Vue from 'vue';
import VueCompositionApi from '@vue/composition-api';
import SitecoreExpEditorPlugin from 'vue-scexpeditor';
import store from '../shared/store';
import { components, directives } from '@/shared/sharedComponents';
import './styles/global.scss';
import '@/shared/styles/global.scss';
import { ScrollUtils } from '@olbbank/vue-components';
import { setCookie } from '@/shared/utils/cookies';
import { getRootDomain } from '@/shared/utils/rootDomain';

Vue.use(VueCompositionApi);
Vue.use(SitecoreExpEditorPlugin);

/**
 *  Fix for Vue with Sitecore Experience Editor
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare let Sitecore: any;
if (typeof Sitecore !== 'undefined') {
	// eslint-disable-next-line no-undef
	document.querySelectorAll("code[type='text/sitecore']").forEach((el) => el.setAttribute('v-pre', ''));

	// DOM contains DocumentFragments which are not searchable via the function above.
	// we need to check all DocumentFragments inside the DOM for Sitecore Experience Editor tags and mark them with v-pre.
	const parser = new DOMParser();
	const templates = document.getElementsByTagName('template');
	if (templates.length > 0) {
		for (let i = 0; i < templates.length; i++) {
			const innerDoc = parser.parseFromString(templates[i].innerHTML, 'text/html');
			innerDoc.querySelectorAll("code[type='text/sitecore']").forEach((el) => el.setAttribute('v-pre', ''));
			templates[i].innerHTML = innerDoc.body.innerHTML;
		}
	}

	Vue.use(SitecoreExpEditorPlugin);
	// eslint-disable-next-line no-undef
	if (Sitecore.PageModes.ChromeManager) {
		// eslint-disable-next-line no-undef
		Sitecore.PageModes.ChromeManager.resetChromes();
	}
}

Vue.config.productionTip = false;

declare const Tc: any;
declare const $: any;
declare const et_vm_reload: any;
declare const favoriteAjaxUrl: string;

/**
 * Window method for async Vue instance injection
 * used by Sitecore Experience Editor `sc/src/OLB.Website/sitecore/shell/Applications/Page Modes/ChromeTypes/PlaceholderChromeType.js`
 */
(window as any).injectComponent = (id: string) => {
	new Vue({
		el: id,
		components: { ...components },
		directives: { ...directives },
	});
};

try {
	new Vue({
		el: '#app',
		store: store,
		components: { ...components },
		directives: { ...directives },
		delimiters: ['{%', '%}'],
		methods: {
			/**
			 * When content of the page changes dynamically,
			 * e.g. markup from a URL is loaded into a dialog,
			 * the etracker has to be reloaded.
			 * See https://www.etracker.com/en/docs/integration-setup-2/tracking-code-sdks/tracking-code-integration/optional-parameters/
			 */
			reloadEtracker() {
				console.log('eTrackContentReload');
				if (typeof et_vm_reload == 'function') {
					et_vm_reload();
					console.log('eTrackContentReload - done');
				}
			},
			/**
			 * Check if current browser is Internet Explorer 11
			 */
			isIe11() {
				// Ignore TypeScript error, that `documentMode` would not exist
				// eslint-disable-next-line @typescript-eslint/ban-ts-ignore
				// @ts-ignore
				const result = !!window.MSInputMethodContext && !!document.documentMode;
				return result;
			},
		},
		data() {
			return {
				/**
				 * Placeholder for template form v-model assigns
				 */
				form: {},
				store: this.$store,
				/**
				 * Global ref access
				 */
				refs: this.$refs,
				/**
				 * Indicates whether the notice is shown
				 */
				isNoticeShown: true,
			};
		},
		created() {
			/**
			 * Event fired by the financebrowser application
			 * indicating the logged-in state with session-expiry timestamp
			 */
			window.addEventListener('sessionStartEvent', (e: any) => {
				this.$store.dispatch('loginStore/updateLoginState', {
					isLoggedIn: true,
					sessionExpires: (e.detail && e.detail.maxInactiveSeconds) || 0,
				});
				setCookie(
					'sessionLength',
					(e.detail && e.detail.maxInactiveSeconds) || '',
					getRootDomain(window.location.host),
					'/',
				);
				setCookie('sessionStartStamp', new Date().toString(), getRootDomain(window.location.host), '/');
				setCookie('isLoggedIn', 'true', getRootDomain(window.location.host), '/');
			});
			/**
			 * Event fired by the financebrowser application
			 * extends the session-expiry timeout
			 */
			window.addEventListener('sessionExtendEvent', async (e: any) => {
				this.$store.dispatch('loginStore/updateLoginState', {
					sessionExpires: (e.detail && e.detail.maxInactiveSeconds) || 0,
				});
				setCookie(
					'sessionLength',
					(e.detail && e.detail.maxInactiveSeconds) || '',
					getRootDomain(window.location.host),
					'/',
				);
				setCookie('sessionStartStamp', new Date().toString(), getRootDomain(window.location.host), '/');
				setCookie('isLoggedIn', 'true', getRootDomain(window.location.host), '/');
			});
			/**
			 * Event fired by the financebrowser application
			 * indicating the logged-out state with session-expiry timestamp
			 */
			window.addEventListener('sessionEndEvent', () => {
				this.$store.dispatch('loginStore/updateLoginState', {
					isLoggedIn: false,
					sessionExpires: 0,
				});
				setCookie('sessionLength', 0, getRootDomain(window.location.host), '/');
				setCookie('sessionStartStamp', new Date().toString(), getRootDomain(window.location.host), '/');
				setCookie('isLoggedIn', 'false', getRootDomain(window.location.host), '/');
			});
			/**
			 * Event fired by the financebrowser application
			 * indicating the logged-out state with session-expiry timestamp
			 */
			window.addEventListener('starterLogoutEvent', () => {
				this.$store.dispatch('loginStore/updateLoginState', {
					isLoggedIn: false,
					sessionExpires: 0,
				});
				setCookie('sessionLength', 0, getRootDomain(window.location.host), '/');
				setCookie('sessionStartStamp', new Date().toString(), getRootDomain(window.location.host), '/');
				setCookie('isLoggedIn', 'false', getRootDomain(window.location.host), '/');
			});
			/**
			 * Event fired by the financebrowser application
			 * indicating that the user has been redirected to the login page due to an expired session
			 */
			window.addEventListener('sessionExpiredEvent', () => {
				this.$store.dispatch('loginStore/updateLoginState', {
					isLoggedIn: false,
					sessionExpires: 0,
					redirectToLogoutPage: false,
				});
				setCookie('sessionLength', 0, getRootDomain(window.location.host), '/');
				setCookie('sessionStartStamp', new Date().toString(), getRootDomain(window.location.host), '/');
				setCookie('isLoggedIn', 'false', getRootDomain(window.location.host), '/');
			});
			/**
			 * Event fired by the financebrowser application
			 * indicating the account-locked state with session-expiry timestamp
			 */
			window.addEventListener('pinLockedEvent', () => {
				this.$store.dispatch('loginStore/updateLoginState', {
					isLoggedIn: false,
				});
				setCookie('sessionLength', 0, getRootDomain(window.location.host), '/');
				setCookie('sessionStartStamp', new Date().toString(), getRootDomain(window.location.host), '/');
				setCookie('isLoggedIn', 'false', getRootDomain(window.location.host), '/');
			});
		},
	});
} catch (e) {
	console.warn(e);
	document.querySelector('[v-cloak]')?.setAttribute('style', 'display: block');
}
